import React, {
  useCallback,
  useMemo,
  useState,
  createElement,
  Component,
} from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { AdminLayout } from '../../components/layout/AdminLayout'
import { useRestaurantBySlug } from '../../lib/hooks'
import { useTenant } from '../../lib/tenant-context'
import {
  useTableManagement,
  useTableMetrics,
  useTableOrderItems,
  closeTableAccount,
} from '../../lib/table-hooks'
import { Card, CardContent } from '../../components/ui/Card'
import { Skeleton } from '../../components/ui/Skeleton'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { PayTableModal } from '../../components/admin/PayTableModal'
import { CabanaAddItemsModal } from '../../components/admin/CabanaAddItemsModal'
import { CabanaModifyOrderModal } from '../../components/admin/CabanaModifyOrderModal'
import { useAdminOrders } from '../../lib/admin-hooks'
import { cn, formatCurrency } from '../../lib/utils'
import { TableWithOrder, TableStatus, OrderChannel } from '../../lib/types'
import {
  Armchair,
  QrCode,
  User,
  Clock,
  ShoppingBag,
  Hash,
  Users,
  Receipt,
  Store,
  Smartphone,
  MonitorSmartphone,
  CreditCard,
  Banknote,
  X,
  Loader2,
  ChefHat,
  Plus,
  Pencil,
} from 'lucide-react'
import { RanchoActionMenu } from '../../components/admin/RanchoActionMenu'
interface Toast {
  id: number
  type: 'success' | 'error'
  message: string
}
export function TableManagementPage() {
  const { slug } = useParams<{
    slug: string
  }>()
  const navigate = useNavigate()
  const { restaurant, loading: restLoading } = useRestaurantBySlug(slug)
  const tenantCtx = useTenant()
  const {
    tables,
    loading: tablesLoading,
    refetch: refetchTables,
  } = useTableManagement(restaurant?.id)
  const { metrics, loading: metricsLoading } = useTableMetrics(restaurant?.id)
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(
    null,
  )
  const [selectedPayment, setSelectedPayment] = useState<
    'cash' | 'card' | 'sinpe'
  >('cash')
  const [isClosing, setIsClosing] = useState(false)
  const [toasts, setToasts] = useState<Toast[]>([])
  const [showPayModal, setShowPayModal] = useState(false)
  const [showAddItemsModal, setShowAddItemsModal] = useState(false)
  const [showModifyModal, setShowModifyModal] = useState(false)
  // Rancho-only: simple action menu state inside the table modal.
  // 'main'  → primary options (Editar / Cobrar)
  // 'edit'  → sub-options under "Editar / Modificar orden"
  const [ranchoMenuMode, setRanchoMenuMode] = useState<'main' | 'edit'>('main')
  // Fetch items only when a table is selected
  const {
    items: orderItems,
    loading: itemsLoading,
    refetch: refetchItems,
  } = useTableOrderItems(selectedTable?.id, restaurant?.id)
  // ─── La Cabaña Parrillera scoping ─────────────────────────────────
  const isCabanaParrillera =
    String(restaurant?.id) === '24' ||
    restaurant?.slug === 'la-cabana-parrillera' ||
    restaurant?.slug === 'cabana-parrillera'
  // ─── Rancho San Francisco scoping ─────────────────────────────────
  const isRanchoSanFrancisco =
    String(restaurant?.id) === '28' ||
    restaurant?.slug === 'rancho-san-francisco'
  // Cabaña + Rancho share the same "modify/cancel" flow — pull cancelOrder
  // from admin hooks only when needed (cheap; just a single supabase listener).
  const { cancelOrder, refetchOrders: refetchAdminOrders } = useAdminOrders(
    isCabanaParrillera || isRanchoSanFrancisco
      ? restaurant?.id
        ? String(restaurant.id)
        : undefined
      : undefined,
  )
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now()
    setToasts((prev) => [
      ...prev,
      {
        id,
        type,
        message,
      },
    ])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3500)
  }, [])
  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])
  const handleCloseAccount = async () => {
    if (!restaurant || !selectedTable || !selectedTable.current_order) return
    if (
      !window.confirm(
        `¿Estás seguro de cerrar la cuenta de la ${selectedTable.table_name || selectedTable.table_number}?`,
      )
    ) {
      return
    }
    setIsClosing(true)
    try {
      const result = await closeTableAccount(
        restaurant.id,
        selectedTable.id,
        selectedPayment,
        tenantCtx?.userId || null,
      )
      if (result.success) {
        addToast('success', 'Cuenta cerrada correctamente')
        setSelectedTable(null)
        refetchTables()
      } else {
        addToast('error', result.error || 'Error al cerrar la cuenta')
      }
    } catch (error: any) {
      addToast('error', error.message || 'Error inesperado')
    } finally {
      setIsClosing(false)
    }
  }
  if (restLoading || !restaurant) {
    return (
      <AdminLayout
        restaurant={restaurant}
        role={tenantCtx?.role}
        onSignOut={tenantCtx?.signOut}
      >
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" />
        </div>
      </AdminLayout>
    )
  }
  const getStatusColor = (status: TableStatus) => {
    switch (status) {
      case 'free':
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
      case 'occupied':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/20'
      case 'ordering':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20'
      case 'waiting_payment':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20'
      case 'closed':
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20'
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    }
  }
  const getStatusLabel = (status: TableStatus) => {
    switch (status) {
      case 'free':
        return 'Disponible'
      case 'occupied':
        return 'Ocupada'
      case 'ordering':
        return 'Ordenando'
      case 'waiting_payment':
        return 'Por Pagar'
      case 'closed':
        return 'Cerrada'
      default:
        return 'Desconocido'
    }
  }
  const getSourceInfo = (channel: OrderChannel | null) => {
    switch (channel) {
      case 'qr':
        return {
          label: 'QR',
          icon: QrCode,
          color: 'bg-emerald-500/20 text-emerald-400',
        }
      case 'waiter':
        return {
          label: 'Mesero',
          icon: User,
          color: 'bg-blue-500/20 text-blue-400',
        }
      case 'cashier':
        return {
          label: 'Caja',
          icon: Store,
          color: 'bg-amber-500/20 text-amber-400',
        }
      case 'web':
        return {
          label: 'Web',
          icon: MonitorSmartphone,
          color: 'bg-gray-500/20 text-gray-400',
        }
      default:
        return {
          label: 'Desconocido',
          icon: Smartphone,
          color: 'bg-gray-500/20 text-gray-400',
        }
    }
  }
  const getItemStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-600'
      case 'preparing':
        return 'bg-amber-100 text-amber-700'
      case 'ready':
        return 'bg-emerald-100 text-emerald-700'
      case 'served':
        return 'bg-blue-100 text-blue-700'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }
  const getItemStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente'
      case 'preparing':
        return 'Preparando'
      case 'ready':
        return 'Listo'
      case 'served':
        return 'Servido'
      default:
        return status
    }
  }
  return (
    <AdminLayout
      restaurant={restaurant}
      role={tenantCtx?.role}
      onSignOut={tenantCtx?.signOut}
    >
      {isRanchoSanFrancisco ? (
        // ─── Rancho San Francisco — Clean big-number mesa grid ─────────
        // Diseñado para señores de 70 años: tipografía grande, alto contraste,
        // un solo gesto (tap en la mesa → modal con todo lo que pueden hacer).
        <div className="min-h-screen bg-[#F5F1EB] p-4 sm:p-8 font-sans">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black text-[#0B0807] tracking-tight">
                  Selecciona una mesa
                </h1>
                <p className="text-[#6B5E4F] text-base sm:text-lg mt-1 font-medium">
                  Toca una mesa para ver, cobrar o modificar la orden
                </p>
              </div>
              <Link
                to={`/admin/${restaurant.slug}/qr`}
                className="flex items-center gap-2 px-5 py-3 bg-white hover:bg-gray-50 text-[#0B0807] font-bold text-base rounded-2xl border-2 border-[#E5DCC9] shadow-sm transition-all"
              >
                <QrCode className="w-5 h-5" />
                Códigos QR
              </Link>
            </div>

            {tablesLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(
                  (i) => (
                    <div
                      key={i}
                      className="aspect-[4/5] rounded-3xl bg-white border-2 border-[#E5DCC9] animate-pulse"
                    />
                  ),
                )}
              </div>
            ) : tables.length === 0 ? (
              <div className="py-24 flex flex-col items-center justify-center text-[#6B5E4F] bg-white rounded-3xl border-2 border-dashed border-[#E5DCC9]">
                <Armchair className="w-20 h-20 text-[#C6A969]/40 mb-4" />
                <h3 className="text-2xl font-black text-[#0B0807] mb-1">
                  No hay mesas configuradas
                </h3>
                <p className="text-base">
                  Corre el script SETUP_RANCHO_TABLES.sql en Supabase para crear
                  las 15 mesas.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5">
                {[...tables]
                  .sort((a, b) => {
                    const na = parseInt(String(a.table_number), 10)
                    const nb = parseInt(String(b.table_number), 10)
                    if (Number.isNaN(na) || Number.isNaN(nb)) {
                      return String(a.table_number).localeCompare(
                        String(b.table_number),
                      )
                    }
                    return na - nb
                  })
                  .map((table) => {
                    const isOccupied = table.status !== 'free'
                    const tableNum = String(table.table_number || '').padStart(
                      2,
                      '0',
                    )
                    return (
                      <button
                        key={table.id}
                        onClick={() => {
                          setRanchoMenuMode('main')
                          setSelectedTable(table)
                        }}
                        className={cn(
                          'aspect-[4/5] bg-white rounded-3xl border-2 flex flex-col items-center justify-center p-4 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-[#C6A969]/40',
                          isOccupied
                            ? 'border-[#F4D35E] bg-[#FFFBEA]'
                            : 'border-[#E5DCC9] hover:border-[#C6A969]',
                        )}
                      >
                        <span className="text-5xl sm:text-6xl font-black text-[#0B0807] leading-none tabular-nums">
                          {tableNum}
                        </span>
                        <span className="text-base font-bold text-[#6B5E4F] mt-3">
                          {table.table_name || `Mesa ${table.table_number}`}
                        </span>
                        <span
                          className={cn(
                            'mt-3 px-4 py-1 rounded-full text-sm font-black',
                            isOccupied
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800',
                          )}
                        >
                          {isOccupied ? 'Ocupada' : 'Libre'}
                        </span>
                        {isOccupied && table.current_order && (
                          <span className="mt-2 text-sm font-black text-[#0B0807] tabular-nums">
                            {formatCurrency(table.current_order.total)}
                          </span>
                        )}
                      </button>
                    )
                  })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans relative">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3">
                Gestión de Mesas
                {!metricsLoading && (
                  <span className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm font-bold tabular-nums">
                    {metrics.activeTables} / {metrics.totalTables} activas
                  </span>
                )}
              </h1>
              <p className="text-white/50 text-sm mt-1">
                Vista general de la operación en salón y autoatención
              </p>
            </div>
            <Link
              to={`/admin/${restaurant.slug}/qr`}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white font-bold text-sm rounded-xl border border-white/10 transition-all"
            >
              <QrCode className="w-4 h-4" />
              Códigos QR
            </Link>
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            {metricsLoading ? (
              [1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-28 rounded-2xl bg-[#141414] animate-pulse border border-white/5"
                />
              ))
            ) : (
              <>
                <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-emerald-500 shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <QrCode className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-2xl font-black tabular-nums">
                      {metrics.qrOrdersToday}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-white/50">
                      Pedidos QR
                    </p>
                    <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md">
                      {metrics.selfServicePercent}% autoatención
                    </span>
                  </div>
                </div>

                <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-blue-500 shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-2xl font-black tabular-nums">
                      {metrics.waiterOrdersToday}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-white/50">
                      Pedidos Mesero
                    </p>
                    <span className="text-xs font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-md">
                      {metrics.waiterPercent}% atención
                    </span>
                  </div>
                </div>

                <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-amber-500 shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Armchair className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="text-2xl font-black tabular-nums">
                      {metrics.activeTables}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-white/50">
                      Mesas Activas
                    </p>
                    <span className="text-xs font-bold text-white/40">
                      de {metrics.totalTables} totales
                    </span>
                  </div>
                </div>

                <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-purple-500 shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Receipt className="w-4 h-4 text-purple-400" />
                    </div>
                    <span className="text-2xl font-black tabular-nums text-[#F4D35E]">
                      {formatCurrency(metrics.averageTicketPerTable)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-white/50">
                      Ticket Promedio
                    </p>
                    <span className="text-xs font-bold text-white/40">
                      por mesa
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Tables Grid */}
          {tablesLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div
                  key={i}
                  className="h-40 rounded-2xl bg-[#141414] animate-pulse border border-white/5"
                />
              ))}
            </div>
          ) : tables.length === 0 ? (
            <div className="py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed">
              <Armchair className="w-16 h-16 text-white/10 mb-4" />
              <h3 className="text-xl font-black text-white/40 mb-1">
                No hay mesas configuradas
              </h3>
              <p className="text-sm">
                Las mesas deben ser creadas desde la configuración del
                restaurante.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {tables.map((table) => {
                const SourceIcon = getSourceInfo(table.order_channel).icon
                const sourceColor = getSourceInfo(table.order_channel).color
                const sourceLabel = getSourceInfo(table.order_channel).label
                const isOccupied = table.status !== 'free'
                return (
                  <div
                    key={table.id}
                    onClick={() => setSelectedTable(table)}
                    className={cn(
                      'bg-[#141414] rounded-2xl p-4 border border-white/5 flex flex-col cursor-pointer transition-all hover:border-white/20 hover:shadow-lg hover:-translate-y-0.5 relative overflow-hidden',
                      isOccupied && 'border-white/10 bg-white/[0.02]',
                    )}
                  >
                    {/* Status Indicator Line */}
                    <div
                      className={cn(
                        'absolute top-0 left-0 right-0 h-1',
                        table.status === 'free' && 'bg-emerald-500',
                        table.status === 'occupied' && 'bg-amber-500',
                        table.status === 'ordering' && 'bg-blue-500',
                        table.status === 'waiting_payment' && 'bg-purple-500',
                        table.status === 'closed' && 'bg-gray-500',
                      )}
                    />

                    <div className="flex justify-between items-start mb-3 mt-1">
                      <div>
                        <h3 className="text-xl font-black text-white leading-none mb-1">
                          {table.table_name || table.table_number}
                        </h3>
                        {table.zone && (
                          <span className="text-xs font-bold text-white/40 uppercase tracking-wider">
                            {table.zone}
                          </span>
                        )}
                      </div>
                      <div
                        className={cn(
                          'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border',
                          getStatusColor(table.status),
                        )}
                      >
                        {getStatusLabel(table.status)}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-white/40 font-medium mb-auto">
                      {table.capacity && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {table.capacity} pax
                        </span>
                      )}
                    </div>

                    {/* Order Info (if occupied) */}
                    <div className="mt-4 pt-3 border-t border-white/5">
                      {isOccupied && table.current_order ? (
                        <>
                          <div className="flex justify-between items-end mb-2">
                            <span className="text-lg font-black text-[#F4D35E] tabular-nums leading-none">
                              {formatCurrency(table.current_order.total)}
                            </span>
                            {table.time_open_minutes !== null && (
                              <span className="flex items-center gap-1 text-xs font-bold text-white/50">
                                <Clock className="w-3.5 h-3.5" />
                                {table.time_open_minutes}m
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/40 font-medium">
                              {table.item_count} items
                            </span>
                            <div className="flex items-center gap-1.5">
                              {(table as any).waiter_name && (
                                <span className="text-[10px] text-white/40 truncate max-w-[60px]">
                                  {(table as any).waiter_name}
                                </span>
                              )}
                              <span
                                className={cn(
                                  'flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
                                  sourceColor,
                                )}
                              >
                                <SourceIcon className="w-3 h-3" />
                                {sourceLabel}
                              </span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-10 text-xs font-bold text-white/20 uppercase tracking-wider">
                          Mesa disponible
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Table Detail Modal */}
      <div className={isRanchoSanFrancisco ? 'text-[#0B0807]' : 'text-white'}>
        <Modal
          isOpen={!!selectedTable}
          onClose={() => {
            setSelectedTable(null)
            setRanchoMenuMode('main')
          }}
          title={
            selectedTable
              ? `Mesa ${selectedTable.table_name || selectedTable.table_number}`
              : ''
          }
        >
          {selectedTable && isRanchoSanFrancisco ? (
            <RanchoActionMenu
              table={selectedTable}
              menuMode={ranchoMenuMode}
              setMenuMode={setRanchoMenuMode}
              onTomarOrden={() => {
                const tn = String(selectedTable.table_number)
                setSelectedTable(null)
                setRanchoMenuMode('main')
                navigate(
                  `/admin/${restaurant.slug}/waiter?mesa=${encodeURIComponent(tn)}`,
                )
              }}
              onAgregarProductos={() => setShowAddItemsModal(true)}
              onModificarProductos={() => setShowModifyModal(true)}
              onCobrarFacturar={() => setShowPayModal(true)}
            />
          ) : selectedTable ? (
            <div className="space-y-6 text-gray-900">
              {/* Table Meta */}
              <div className="flex flex-wrap gap-2">
                <Badge
                  className={cn(
                    'border-0',
                    selectedTable.status === 'free' &&
                      'bg-emerald-100 text-emerald-800',
                    selectedTable.status === 'occupied' &&
                      'bg-amber-100 text-amber-800',
                    selectedTable.status === 'ordering' &&
                      'bg-blue-100 text-blue-800',
                    selectedTable.status === 'waiting_payment' &&
                      'bg-purple-100 text-purple-800',
                    selectedTable.status === 'closed' &&
                      'bg-gray-100 text-gray-800',
                  )}
                >
                  {getStatusLabel(selectedTable.status)}
                </Badge>
                {selectedTable.zone && (
                  <Badge variant="secondary">{selectedTable.zone}</Badge>
                )}
                {selectedTable.capacity && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    <Users className="w-3 h-3" />
                    {selectedTable.capacity} pax
                  </Badge>
                )}
              </div>

              {selectedTable.current_order ? (
                <>
                  {/* Order Summary */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Orden
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5">
                          <Hash className="w-4 h-4 text-gray-400" />
                          {selectedTable.current_order.order_number}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Cliente
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5 truncate">
                          <User className="w-4 h-4 text-gray-400" />
                          {selectedTable.current_order.customer_name ||
                            'Sin nombre'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Origen
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5">
                          {createElement(
                            getSourceInfo(selectedTable.order_channel).icon,
                            {
                              className: 'w-4 h-4 text-gray-400',
                            },
                          )}
                          {getSourceInfo(selectedTable.order_channel).label}
                          {(selectedTable as any).waiter_name && (
                            <span className="text-gray-500 font-normal text-sm ml-1 truncate max-w-[100px]">
                              ({(selectedTable as any).waiter_name})
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Tiempo
                        </p>
                        <p className="font-bold text-gray-900 flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-gray-400" />
                          {selectedTable.time_open_minutes} min
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Items List */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-gray-400" />
                      Items del Pedido ({selectedTable.item_count})
                    </h4>
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2">
                      {itemsLoading ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
                        </div>
                      ) : orderItems.length > 0 ? (
                        orderItems.map((item) => (
                          <div
                            key={item.order_item_id}
                            className="flex justify-between items-start py-3 border-b border-gray-100 last:border-0"
                          >
                            <div className="flex gap-3">
                              <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-sm h-fit">
                                {item.quantity}x
                              </span>
                              <div>
                                <p className="font-bold text-gray-900 text-sm">
                                  {item.item_name}
                                </p>
                                <div className="flex items-center gap-2 mt-1.5">
                                  {item.station_name && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                      <ChefHat className="w-3 h-3" />
                                      {item.station_name}
                                    </span>
                                  )}
                                  <span
                                    className={cn(
                                      'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
                                      getItemStatusBadge(item.item_status),
                                    )}
                                  >
                                    {getItemStatusLabel(item.item_status)}
                                  </span>
                                </div>
                                {item.notes && (
                                  <p className="text-xs text-gray-500 mt-1.5 italic">
                                    "{item.notes}"
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className="font-bold text-gray-900 text-sm tabular-nums shrink-0">
                              {formatCurrency(item.line_total)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 italic py-2">
                          No se pudieron cargar los items.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Total & Payment */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center mb-6">
                      <span className="font-bold text-gray-900">
                        Total a pagar
                      </span>
                      <span className="text-2xl font-black text-brand-amber tabular-nums">
                        {formatCurrency(selectedTable.current_order.total)}
                      </span>
                    </div>

                    {/* Cabaña + Rancho — Adicionar productos a la mesa */}
                    {(isCabanaParrillera || isRanchoSanFrancisco) &&
                      selectedTable.current_order.status !== 'delivered' &&
                      selectedTable.current_order.status !== 'cancelled' && (
                        <Button
                          fullWidth
                          size="lg"
                          variant="outline"
                          onClick={() => setShowAddItemsModal(true)}
                          className="mb-3 border-2 border-amber-400 text-amber-700 hover:bg-amber-50 font-bold flex items-center justify-center gap-2"
                        >
                          <Plus className="w-5 h-5" />
                          Adicionar productos a la mesa
                        </Button>
                      )}

                    {/* Cabaña + Rancho — Modificar / Eliminar productos */}
                    {(isCabanaParrillera || isRanchoSanFrancisco) &&
                      selectedTable.current_order.status !== 'delivered' &&
                      selectedTable.current_order.status !== 'cancelled' && (
                        <Button
                          fullWidth
                          size="lg"
                          variant="outline"
                          onClick={() => setShowModifyModal(true)}
                          className="mb-3 border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-bold flex items-center justify-center gap-2"
                        >
                          <Pencil className="w-5 h-5" />
                          Modificar / Eliminar productos
                        </Button>
                      )}

                    {/* Primary: Cobrar y Facturar */}
                    <Button
                      fullWidth
                      size="lg"
                      onClick={() => setShowPayModal(true)}
                      className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-lg flex items-center justify-center gap-2"
                    >
                      <Receipt className="w-5 h-5" />
                      Cobrar y Facturar
                    </Button>

                    <details className="mt-3">
                      <summary className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer text-center">
                        Cerrar sin facturar (legacy)
                      </summary>
                      <div className="mt-3 space-y-3">
                        <p className="text-xs font-bold text-gray-500">
                          Método de pago
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          <label
                            className={cn(
                              'flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all',
                              selectedPayment === 'cash'
                                ? 'border-brand-amber bg-amber-50 text-brand-amber'
                                : 'border-gray-100 hover:border-gray-200 text-gray-600',
                            )}
                          >
                            <input
                              type="radio"
                              name="payment"
                              value="cash"
                              checked={selectedPayment === 'cash'}
                              onChange={() => setSelectedPayment('cash')}
                              className="sr-only"
                            />
                            <Banknote className="w-5 h-5 mb-1" />
                            <span className="text-xs font-bold">Efectivo</span>
                          </label>
                          <label
                            className={cn(
                              'flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all',
                              selectedPayment === 'card'
                                ? 'border-brand-amber bg-amber-50 text-brand-amber'
                                : 'border-gray-100 hover:border-gray-200 text-gray-600',
                            )}
                          >
                            <input
                              type="radio"
                              name="payment"
                              value="card"
                              checked={selectedPayment === 'card'}
                              onChange={() => setSelectedPayment('card')}
                              className="sr-only"
                            />
                            <CreditCard className="w-5 h-5 mb-1" />
                            <span className="text-xs font-bold">Tarjeta</span>
                          </label>
                          <label
                            className={cn(
                              'flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all',
                              selectedPayment === 'sinpe'
                                ? 'border-brand-amber bg-amber-50 text-brand-amber'
                                : 'border-gray-100 hover:border-gray-200 text-gray-600',
                            )}
                          >
                            <input
                              type="radio"
                              name="payment"
                              value="sinpe"
                              checked={selectedPayment === 'sinpe'}
                              onChange={() => setSelectedPayment('sinpe')}
                              className="sr-only"
                            />
                            <Smartphone className="w-5 h-5 mb-1" />
                            <span className="text-xs font-bold">SINPE</span>
                          </label>
                        </div>
                        <Button
                          fullWidth
                          size="md"
                          onClick={handleCloseAccount}
                          disabled={isClosing}
                          className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                          {isClosing ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Cerrando cuenta...
                            </span>
                          ) : (
                            'Cerrar Cuenta'
                          )}
                        </Button>
                      </div>
                    </details>
                  </div>
                </>
              ) : (
                <div className="py-12 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-xl border border-gray-100 border-dashed">
                  <Armchair className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="font-medium text-gray-600">Mesa disponible</p>
                  <p className="text-sm">
                    No hay ninguna orden activa en esta mesa.
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </Modal>
      </div>

      {/* Pay Modal — split bill + auto invoice */}
      <PayTableModal
        isOpen={showPayModal}
        onClose={() => setShowPayModal(false)}
        table={selectedTable}
        restaurantId={restaurant?.id}
        createdBy={tenantCtx?.userId || null}
        onSuccess={(allPaid) => {
          addToast(
            'success',
            allPaid
              ? 'Cuenta cerrada y factura emitida'
              : 'Pago parcial registrado — saldo pendiente',
          )
          refetchTables()
          if (allPaid) {
            setShowPayModal(false)
            setSelectedTable(null)
            setRanchoMenuMode('main')
          }
        }}
      />

      {/* Cabaña + Rancho — Adicionar productos a la mesa */}
      {(isCabanaParrillera || isRanchoSanFrancisco) && (
        <CabanaAddItemsModal
          isOpen={showAddItemsModal}
          onClose={() => setShowAddItemsModal(false)}
          orderId={selectedTable?.current_order?.id || null}
          restaurantId={restaurant?.id}
          onAdded={() => {
            addToast('success', 'Productos adicionados a la mesa')
            refetchTables()
            refetchItems?.()
          }}
        />
      )}

      {/* Cabaña + Rancho — Modificar / Eliminar productos / Cancelar orden */}
      {(isCabanaParrillera || isRanchoSanFrancisco) && (
        <CabanaModifyOrderModal
          isOpen={showModifyModal}
          onClose={() => setShowModifyModal(false)}
          orderId={selectedTable?.current_order?.id || null}
          onChanged={() => {
            addToast('success', 'Orden actualizada')
            refetchTables()
            refetchItems?.()
            refetchAdminOrders?.()
          }}
          onCancelOrder={async (orderId) => {
            const res = await cancelOrder(orderId)
            if (res.success) {
              addToast('success', '❌ Pedido cancelado')
              setShowModifyModal(false)
              setSelectedTable(null)
              setRanchoMenuMode('main')
              refetchTables()
            } else if (res.error) {
              addToast('error', res.error)
            }
            return res
          }}
        />
      )}

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={cn(
                'pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up',
                toast.type === 'success'
                  ? 'bg-emerald-600 border-emerald-500 text-white'
                  : 'bg-red-600 border-red-500 text-white',
              )}
            >
              <span className="flex-1">{toast.message}</span>
              <button
                onClick={() => removeToast(toast.id)}
                className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  )
}
// ─── Rancho San Francisco — Action menu inside the mesa modal ───────
// Diseñado para señores de 70 años: una sola acción visible a la vez,
// botones grandes, copy en español claro, sub-menú para "Editar".
interface RanchoActionMenuProps {
  table: TableWithOrder
  menuMode: 'main' | 'edit'
  setMenuMode: (m: 'main' | 'edit') => void
  onTomarOrden: () => void
  onAgregarProductos: () => void
  onModificarProductos: () => void
  onCobrarFacturar: () => void
}
function RanchoActionMenu({
  table,
  menuMode,
  setMenuMode,
  onTomarOrden,
  onAgregarProductos,
  onModificarProductos,
  onCobrarFacturar,
}: RanchoActionMenuProps) {
  const isOccupied = table.status !== 'free' && !!table.current_order
  // ── Mesa libre: una sola opción, Tomar orden ──────────────────────
  if (!isOccupied) {
    return (
      <div className="space-y-5 text-[#0B0807]">
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-100">
          <Armchair className="w-6 h-6 text-emerald-600" />
          <div>
            <p className="font-bold text-emerald-800 leading-tight">
              Mesa libre
            </p>
            <p className="text-sm text-emerald-700">
              Esta mesa no tiene una orden abierta.
            </p>
          </div>
        </div>
        <button
          onClick={onTomarOrden}
          className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-[#0B0807] hover:bg-black text-white font-black text-xl shadow-lg transition-all hover:-translate-y-0.5"
        >
          <ShoppingBag className="w-7 h-7" />
          Tomar orden
        </button>
      </div>
    )
  }
  // ── Mesa ocupada: menú principal o sub-menú de Editar ─────────────
  return (
    <div className="space-y-5 text-[#0B0807]">
      {/* Resumen mínimo de la mesa ocupada */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200">
        <div className="flex items-center gap-3">
          <Receipt className="w-6 h-6 text-amber-700" />
          <div>
            <p className="font-bold text-amber-900 leading-tight">
              Mesa ocupada
            </p>
            {table.current_order && (
              <p className="text-sm text-amber-800 tabular-nums">
                Total:{' '}
                <strong>{formatCurrency(table.current_order.total)}</strong>
                {typeof table.item_count === 'number' && (
                  <>
                    {' · '}
                    {table.item_count} items
                  </>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {menuMode === 'main' ? (
        <div className="space-y-3">
          <button
            onClick={() => setMenuMode('edit')}
            className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-white border-2 border-blue-300 hover:border-blue-500 hover:bg-blue-50 transition-all text-left shadow-sm"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <Pencil className="w-6 h-6 text-blue-700" />
            </div>
            <div className="flex-1">
              <p className="font-black text-lg text-[#0B0807] leading-tight">
                Editar / Modificar orden
              </p>
              <p className="text-sm text-gray-600">
                Agregar o quitar productos de la mesa
              </p>
            </div>
          </button>

          <button
            onClick={onCobrarFacturar}
            className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-amber-500 hover:bg-amber-600 transition-all text-left shadow-lg"
          >
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-black text-lg text-white leading-tight">
                Cobrar y Facturar
              </p>
              <p className="text-sm text-white/90">
                Recibir el pago y emitir la factura
              </p>
            </div>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <button
            onClick={() => setMenuMode('main')}
            className="text-sm font-bold text-gray-500 hover:text-gray-800 flex items-center gap-1.5"
          >
            ← Volver
          </button>

          <button
            onClick={onAgregarProductos}
            className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-white border-2 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left shadow-sm"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <Plus className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="flex-1">
              <p className="font-black text-lg text-[#0B0807] leading-tight">
                Agregar productos
              </p>
              <p className="text-sm text-gray-600">
                Añadir más cosas a la cuenta de la mesa
              </p>
            </div>
          </button>

          <button
            onClick={onModificarProductos}
            className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-white border-2 border-red-200 hover:border-red-400 hover:bg-red-50 transition-all text-left shadow-sm"
          >
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
              <X className="w-6 h-6 text-red-700" />
            </div>
            <div className="flex-1">
              <p className="font-black text-lg text-[#0B0807] leading-tight">
                Quitar productos / cancelar
              </p>
              <p className="text-sm text-gray-600">
                Eliminar productos o cancelar la orden completa
              </p>
            </div>
          </button>
        </div>
      )}
    </div>
  )
}
